import type {
  DeepPartial,
  MessageWidgetStyles,
  MultipleChoiceWidgetStyles,
  TimelineEntity,
  ToolWidgetStyles,
} from './types';

export type EntityKind = TimelineEntity['kind'];

export type WidgetRenderer<K extends EntityKind = EntityKind> = (args: {
  entity: Extract<TimelineEntity, { kind: K }>;
  debugMode: boolean;
  hideRoleLabels: boolean;
  hideTimestamps: boolean;
  convId?: string;
  userBgColor?: string;
  assistantBgColor?: string;
  toolWidgetStyles?: DeepPartial<ToolWidgetStyles>;
  messageWidgetStyles?: DeepPartial<MessageWidgetStyles>;
  multipleChoiceWidgetStyles?: DeepPartial<MultipleChoiceWidgetStyles>;
}) => React.ReactNode;

const registry = new Map<EntityKind, WidgetRenderer<any>>();
type Visibility = { normal?: boolean; debug?: boolean };
const visibilityRegistry = new Map<EntityKind, Required<Visibility>>();

export type Mode = 'normal' | 'debug';

export interface WidgetRegistrationOptions {
  visibility?: Visibility;
}

export function registerWidgetRenderer<K extends EntityKind>(
  kind: K,
  renderer: WidgetRenderer<K>,
  options?: WidgetRegistrationOptions
): void {
  registry.set(kind, renderer as WidgetRenderer<any>);
  const v = options?.visibility || {};
  visibilityRegistry.set(kind, {
    normal: v.normal === undefined ? false : !!v.normal,
    debug: v.debug === undefined ? true : !!v.debug,
  });
}

export function getWidgetRenderer(kind: EntityKind): WidgetRenderer<any> | undefined {
  return registry.get(kind);
}

export function clearWidgetRenderers(): void {
  registry.clear();
  visibilityRegistry.clear();
}

export function listRegisteredWidgetKinds(): EntityKind[] {
  return Array.from(registry.keys());
}

export function getVisibility(kind: EntityKind): Required<Visibility> {
  return visibilityRegistry.get(kind) || { normal: false, debug: true };
}

export function filterByMode(entities: TimelineEntity[], mode: Mode): TimelineEntity[] {
  return entities.filter((e) => {
    const v = getVisibility(e.kind as EntityKind);
    return mode === 'normal' ? v.normal : v.debug;
  });
}
